// ONE LABEL FOR A SCHEDULE ACTIVITY, USED BY EVERY DROPDOWN.
//
// Owner, 2026-09-29: "use the activity parent name.childname.childname.."
// and the reason, which decides every judgement call here: "to remove
// confusion from seeing level3 3 times in a dropdown". THE GOAL IS
// DISAMBIGUATION, NOT DECORATION.
//
// WHY IT IS ONE FUNCTION: there were at least eleven places building an
// activity label, each its own way, which is why the same activity read
// differently depending which page you were on. The truncation rule lives
// here and nowhere else.
//
// WHY NOT THE WBS CODE, which is what he asked for first: the data refused
// it. SpaceX BA03 has 575 rows and only 157 carry a wbs, and NONE of the
// worst duplicates do - "Electrical · Conduit" appears sixteen times with no
// code on any of them. Names disambiguate there; codes would not.
//
// THE SHAPE HE CHOSE, after seeing the measurements: root, an ellipsis, then
// the last two levels. The full path on BA03 has a MEDIAN OF 112 CHARACTERS
// and reaches 282, with 449 of 575 rows four or more deep - wider than the
// control that has to show it.
//
// AND THE ROOT IS KEPT BECAUSE THE DATA SAYS IT IS THE DISAMBIGUATOR: of 59
// duplicated names on BA03, 48 FIRST DIFFER AT THE ROOT and 8 at the level
// below. Truncating from the front would have thrown away exactly what tells
// them apart - which is why this keeps the root and elides the middle.
const SEP = ".";
const ELLIPSIS = "…";

const clean = (v) => String(v == null ? "" : v).trim();

/** Root first, the activity's own name last. */
export function activityPath(activity) {
  if (!activity) return [];
  const anc = Array.isArray(activity.path) ? activity.path.map(clean).filter(Boolean) : [];
  const own = clean(activity.name);
  return own ? [...anc, own] : anc;
}

/**
 * The label a dropdown shows. `full` gives the whole path for a tooltip or
 * a title attribute, so nothing is ever unreachable - only unshown.
 */
export function activityLabel(activity, { maxParts = 3 } = {}) {
  const parts = activityPath(activity);
  if (!parts.length) return "";
  const full = parts.join(SEP);
  if (parts.length <= maxParts) return full;
  // Root, then the ellipsis, then the last two.
  //
  // THE COUNT THAT DECIDED THIS, kept here rather than only in the commit
  // message, because the obvious way to shorten a path is to truncate from
  // the FRONT and that is the one shape that destroys the information:
  // of 59 duplicated activity names on BA03, 48 FIRST DIFFER AT THE ROOT
  // and 8 one level below. Dropping the root would have removed exactly
  // what tells them apart, in 48 of 59 cases.
  return [parts[0], ELLIPSIS, parts[parts.length - 2], parts[parts.length - 1]].join(SEP);
}

/** The whole path, for a title attribute. Never truncated. */
export function activityTitle(activity) {
  return activityPath(activity).join(SEP);
}

export const ACTIVITY_LABEL_SEP = SEP;

// ---- THE FULL PATH, THE DIGEST'S WAY ---------------------------------------
//
// Owner, 2026-10-13: "in the area/levels and other edit or create shop
// drawings, show full path like we do on digest so user can understand which
// activity parent he is working in."
//
// THE SERVER BUILDS IT. back/utils/activityPath formatPath is the digest's own
// builder ("BIM / Shop drawings / Level 1 / L1 · Area A / ..."), and every shop
// drawing payload carries its result as `fullPath` (an activity) or
// `activityFullPath` (a drawing). This prints that string and builds nothing.
//
// The join below is ONLY for a list the client walked itself (the bulk sheet
// import holds the whole plan), and it is pinned to the server's separator by
// src/__tests__/activityFullPath.test.js, which requires the back-end builder
// and compares the two outputs.
export const ACTIVITY_PATH_SEP = " / ";

export function activityFullPath(activity) {
  if (!activity) return "";
  const sent = clean(activity.fullPath || activity.activityFullPath);
  if (sent) return sent;
  return activityPath(activity).join(ACTIVITY_PATH_SEP);
}

/**
 * Add `path` (ancestor NAMES, root first) to every row of a schedule list.
 *
 * CALL THIS ON THE FULL LIST, BEFORE ANY FILTER. Every consumer of these
 * lists filters - `!it.disabled`, `status !== "done"`, a partner scope on
 * the server - and an ancestor removed by the filter is an ancestor the
 * walk cannot resolve. Derive first, filter second.
 *
 * WHERE A PARENT CANNOT BE RESOLVED THE WALK STOPS rather than skipping
 * that level and carrying on. A partner is served a SCOPED list, so gaps
 * are normal there; joining across one would read as "BIM · Conduit" for a
 * row that actually lives four levels down, and a label that invents a
 * shorter hierarchy is worse than a label that shows less of a true one.
 * What comes back is always a contiguous run of real ancestors.
 */
export function withActivityPaths(items) {
  const list = Array.isArray(items) ? items : [];
  const byId = new Map();
  list.forEach((it) => { if (it && it._id) byId.set(String(it._id), it); });
  return list.map((it) => {
    if (!it) return it;
    const out = [];
    let cur = it.parentId ? byId.get(String(it.parentId)) : null;
    const seen = new Set([String(it._id)]);
    for (let guard = 0; cur && guard < 12; guard += 1) {
      if (seen.has(String(cur._id))) break; // a cycle in the data is not a reason to hang
      seen.add(String(cur._id));
      out.unshift(clean(cur.name));
      cur = cur.parentId ? byId.get(String(cur.parentId)) : null;
    }
    return { ...it, path: out.filter(Boolean) };
  });
}
